import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dropdown, Tooltip, Form, message } from 'antd';
import { MoreOutlined, CopyOutlined } from '@ant-design/icons';
import { useCanCheck } from '@/ability/helpers';
import StatusBadge from '@/components/ui/StatusBadge';
import { getEmployees, getEmployeeById } from '../services/employee.service';
import { getSchedules } from '../../schedule/services/schedule.service';
import { createUser, updateUser, requestRemoval, deleteUser } from '@services/users.service';
import { getRoles } from '@services/rbac.service';
import { useAuthStore } from '@stores/auth.store';
import { EMPLOYEES_QUERY_KEY, EMPLOYEE_DETAIL_QUERY_KEY } from './employeesQueryKeys';
import { getEmployeeColumns } from '../columns/employeeColumns';
import Link from '@/components/ui/Link';
import { useNavigate } from 'react-router-dom';

const ROLES_QUERY_KEY = ['rbac', 'roles'];

const STATUS_TAB = { ALL: 'all', ACTIVE: 'active', INACTIVE: 'inactive' };
const capFirst = (s) => (s && s.length ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '—');

/** Normalize role id from API (id, roleId, or role.id) for consistent comparison. */
const getRoleId = (r) => (r && (r.id ?? r.roleId ?? r.role?.id)) ?? null;
/** Normalize role name for comparison (handles id vs label mix-up in filter value). */
const getRoleName = (r) => (r && (r.name ?? r.roleName ?? r.label ?? r.role?.name)) ?? '';
/** Match employee roles to filter: filterValue can be role id or role name (case-insensitive). */
const roleMatchesFilter = (roles, filterValue) => {
    if (filterValue == null || filterValue === '' || !Array.isArray(roles) || roles.length === 0) return false;
    const want = String(filterValue).trim().toLowerCase();
    if (!want) return false;
    return roles.some((r) => {
        const id = getRoleId(r);
        const name = getRoleName(r);
        return (
            (id != null && String(id) === String(filterValue).trim()) ||
            (name && String(name).toLowerCase() === want)
        );
    });
};

/**
 * Fetch single employee by id. Export for use in EmployeeDetailPanel.
 */
export const useEmployeeDetail = (employeeId) =>
    useQuery({
        queryKey: EMPLOYEE_DETAIL_QUERY_KEY(employeeId),
        queryFn: () => getEmployeeById(employeeId),
        enabled: !!employeeId,
    });

/**
 * All employees list logic: data, filters, table config, panel state, permissions, handlers.
 * UI should only consume this hook and render.
 */
export const useEmployees = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [createForm] = Form.useForm();
    const [searchText, setSearchText] = useState('');
    const [statusTab, setStatusTab] = useState(STATUS_TAB.ALL);
    const [filterRole, setFilterRole] = useState(undefined);
    const [filterStatus, setFilterStatus] = useState(undefined);
    const [panelOpen, setPanelOpen] = useState(false);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [removalConfirmOpen, setRemovalConfirmOpen] = useState(false);
    const [removeUserModalOpen, setRemoveUserModalOpen] = useState(false);
    const [employeeForEdit, setEmployeeForEdit] = useState(null);
    const [employeeForRemoval, setEmployeeForRemoval] = useState(null);
    const [employeeForRemoveUser, setEmployeeForRemoveUser] = useState(null);
    const [editForm] = Form.useForm();

    const can = useCanCheck();
    const authRole = useAuthStore((s) => s.role);
    const authRoles = useAuthStore((s) => s.roles ?? []);
    const isCompanyAdmin =
        authRole === 'Company Admin' || (Array.isArray(authRoles) && authRoles.includes('Company Admin'));
    const canView = can('hr.employees.view');
    const canViewAll = can('hr.employees.view_all');
    const canCreate = can('hr.employees.create');
    const canUpdate = can('hr.employees.update');
    const canDelete = can('hr.employees.delete');
    const canRemove = can('hr.employees.remove');
    const canRemoveUser = can('saas.users.delete');
    /** HR-only removal request; Company Admin removes directly (Remove User), not via pending request. */
    const canRequestHrRemoval = canRemove && !isCompanyAdmin;

    const { data: response, isError: employeesError } = useQuery({
        queryKey: [...EMPLOYEES_QUERY_KEY, { page: 1, limit: 100 }],
        queryFn: () => getEmployees({ page: 1, limit: 100 }),
        enabled: !!canView && !!canViewAll,
        retry: false,
    });

    const { data: schedulesResponse } = useQuery({
        queryKey: ['hr', 'schedules', { page: 1, limit: 100 }],
        queryFn: () => getSchedules({ page: 1, limit: 100 }),
        enabled: !!(canView && (createModalOpen || editModalOpen)),
    });
    const schedulesList = schedulesResponse?.data ?? [];

    const { data: rolesRaw } = useQuery({
        queryKey: ROLES_QUERY_KEY,
        queryFn: () => getRoles(),
        enabled: !!(can('saas.roles.view') && (createModalOpen || editModalOpen)),
    });
    const roles = Array.isArray(rolesRaw) ? rolesRaw : (rolesRaw?.data ?? []);

    const createEmployeeMutation = useMutation({
        mutationFn: (body) => createUser(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: EMPLOYEES_QUERY_KEY });
        },
    });

    const updateEmployeeMutation = useMutation({
        mutationFn: ({ id, body }) => updateUser(id, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: EMPLOYEES_QUERY_KEY });
            if (selectedEmployeeId) {
                queryClient.invalidateQueries({ queryKey: EMPLOYEE_DETAIL_QUERY_KEY(selectedEmployeeId) });
            }
        },
    });

    const requestRemovalMutation = useMutation({
        mutationFn: (userId) => requestRemoval(userId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: EMPLOYEES_QUERY_KEY });
            if (selectedEmployeeId) {
                queryClient.invalidateQueries({ queryKey: EMPLOYEE_DETAIL_QUERY_KEY(selectedEmployeeId) });
            }
        },
    });

    const deleteUserMutation = useMutation({
        mutationFn: (userId) => deleteUser(userId),
        onSuccess: (_, userId) => {
            queryClient.invalidateQueries({ queryKey: EMPLOYEES_QUERY_KEY });
            setRemoveUserModalOpen(false);
            setEmployeeForRemoveUser(null);
            if (selectedEmployeeId === userId) {
                setPanelOpen(false);
                setSelectedEmployeeId(null);
            }
        },
    });

    const employees = response?.data ?? [];
    const total = response?.total ?? 0;
    const activeCount = useMemo(
        () => employees.filter((e) => (e.status || '').toLowerCase() === 'active').length,
        [employees],
    );

    const selectedEmployeeName = useMemo(
        () => employees.find((e) => e.id === selectedEmployeeId)?.name ?? 'Employee details',
        [employees, selectedEmployeeId],
    );

    const filterRoleOptions = useMemo(() => {
        const list = Array.isArray(roles) ? roles : [];
        return list.map((r) => {
            const name = (r && (r.name ?? r.roleName ?? r.label)) || r?.id || '';
            return { value: name, label: name };
        });
    }, [roles]);

    const filterStatusOptions = useMemo(
        () => [
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
        ],
        [],
    );

    const activeFilterCount = [filterRole, filterStatus].filter(Boolean).length;

    const clearFilters = useCallback(() => {
        setFilterRole(undefined);
        setFilterStatus(undefined);
    }, []);

    const filteredEmployees = useMemo(() => {
        let list = employees;
        if (statusTab === STATUS_TAB.ACTIVE) list = list.filter((e) => (e.status || '').toLowerCase() === 'active');
        if (statusTab === STATUS_TAB.INACTIVE) list = list.filter((e) => (e.status || '').toLowerCase() !== 'active');
        if (filterRole) list = list.filter((e) => roleMatchesFilter(e.roles, filterRole));
        if (filterStatus) list = list.filter((e) => (e.status || '').toLowerCase() === filterStatus);
        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            list = list.filter(
                (e) => (e.name || '').toLowerCase().includes(q) || (e.email || '').toLowerCase().includes(q),
            );
        }
        return list;
    }, [employees, statusTab, filterRole, filterStatus, searchText]);

    const tableData = useMemo(
        () =>
            filteredEmployees.map((e) => ({
                key: e.id,
                id: e.id,
                name: e.name,
                email: e.email,
                status: e.status,
                roles: e.roles,
                lastLogin: e.lastLogin,
                removalRequest: e.removalRequest,
                scheduleGroup: e.scheduleGroup,
                thumbId: e.thumbId,
                rfid: e.rfid,
            })),
        [filteredEmployees],
    );

    const handleActionClick = (menuInfo, record) => {
        if (menuInfo.key === 'view') {
            openPanel(record.id);
        }
        if (menuInfo.key === 'attendance_detail') {
            navigate(`/hr/attendance/detail?userId=${record.id}`);
        }
        if (menuInfo.key === 'edit') {
            setEmployeeForEdit(record);
            const roleId = record.roles?.[0]?.id;
            editForm.setFieldsValue({
                roleId: roleId ?? undefined,
                status: record.status ?? 'active',
                scheduleGroupId: record.scheduleGroup?.id ?? undefined,
            });
            setEditModalOpen(true);
        }
        if (menuInfo.key === 'request_removal') {
            setEmployeeForRemoval(record);
            setRemovalConfirmOpen(true);
        }
        if (menuInfo.key === 'remove_user') {
            setEmployeeForRemoveUser(record);
            setRemoveUserModalOpen(true);
        }
    };

    const openEditModal = useCallback(
        (employee) => {
            if (!employee) return;
            setEmployeeForEdit(employee);
            const roleId = employee.roles?.[0]?.id;
            editForm.setFieldsValue({
                roleId: roleId ?? undefined,
                status: employee.status ?? 'active',
                scheduleGroupId: employee.scheduleGroup?.id ?? undefined,
            });
            setEditModalOpen(true);
        },
        [editForm],
    );

    const closeEditModal = useCallback(() => {
        setEditModalOpen(false);
        setEmployeeForEdit(null);
        editForm.resetFields();
    }, [editForm]);

    const onSubmitEdit = useCallback(() => {
        if (!employeeForEdit?.id) return;
        editForm.validateFields().then((values) => {
            updateEmployeeMutation.mutate(
                {
                    id: employeeForEdit.id,
                    body: {
                        roleId: values.roleId,
                        status: values.status,
                        scheduleGroupId: values.scheduleGroupId || null,
                    },
                },
                {
                    onSuccess: () => {
                        message.success('Employee updated.');
                        closeEditModal();
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || 'Failed to update employee.');
                    },
                },
            );
        });
    }, [employeeForEdit, editForm, updateEmployeeMutation, closeEditModal]);

    const confirmRequestRemoval = useCallback(() => {
        if (!employeeForRemoval?.id) return;
        requestRemovalMutation.mutate(employeeForRemoval.id, {
            onSuccess: () => {
                message.success('Removal requested. Company Admin must approve.');
                setRemovalConfirmOpen(false);
                setEmployeeForRemoval(null);
            },
            onError: (err) => {
                message.error(err?.response?.data?.message || 'Failed to request removal.');
            },
        });
    }, [employeeForRemoval, requestRemovalMutation]);

    const closeRemovalConfirm = useCallback(() => {
        setRemovalConfirmOpen(false);
        setEmployeeForRemoval(null);
    }, []);

    const openRemovalConfirm = useCallback((employee) => {
        setEmployeeForRemoval(employee);
        setRemovalConfirmOpen(true);
    }, []);

    const openRemoveUserConfirm = useCallback((employee) => {
        setEmployeeForRemoveUser(employee);
        setRemoveUserModalOpen(true);
    }, []);

    const closeRemoveUserModal = useCallback(() => {
        setRemoveUserModalOpen(false);
        setEmployeeForRemoveUser(null);
    }, []);

    const confirmRemoveUser = useCallback(() => {
        if (!employeeForRemoveUser?.id) return;
        deleteUserMutation.mutate(employeeForRemoveUser.id, {
            onSuccess: () => message.success('User removed from company.'),
            onError: (err) =>
                message.error(err?.response?.data?.message || 'Failed to remove user.'),
        });
    }, [employeeForRemoveUser, deleteUserMutation]);

    const handleCopyEmail = useCallback((email, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (!email) return;
        navigator.clipboard.writeText(email);
        message.success('Email copied to clipboard.');
    }, []);

    const hasPendingRemoval = (record) => {
        const status = record.removalRequest?.status;
        return (status || '').toLowerCase() === 'pending';
    };

    const columns = useMemo(
        () =>
            getEmployeeColumns({
                handleCopyEmail,
                handleActionClick,
                canUpdate,
                canDelete,
                canRequestHrRemoval,
                canRemoveUser,
                hasPendingRemoval,
            }),
        [canUpdate, canDelete, canRemoveUser, canRequestHrRemoval, handleActionClick, handleCopyEmail],
    );

    const openPanel = (id) => {
        setSelectedEmployeeId(id);
        setPanelOpen(true);
    };
    const closePanel = () => {
        setPanelOpen(false);
        setSelectedEmployeeId(null);
    };

    const closeCreateModal = useCallback(() => {
        setCreateModalOpen(false);
        createForm.resetFields();
    }, [createForm]);

    const onSubmitCreate = useCallback(() => {
        createForm.validateFields().then((values) => {
            const payload = {
                email: values.email?.trim(),
                name: values.name?.trim(),
                password: values.password,
                roleId: values.roleId,
                scheduleGroupId: values.scheduleGroupId || undefined,
            };
            createEmployeeMutation.mutate(payload, {
                onSuccess: () => {
                    message.success('Employee created. They will receive a verification email.');
                    closeCreateModal();
                },
                onError: (err) => {
                    message.error(err?.response?.data?.message || 'Failed to create employee.');
                },
            });
        });
    }, [createForm, createEmployeeMutation, closeCreateModal]);

    const roleOptions = useMemo(() => {
        const list = Array.isArray(roles) ? roles : [];
        const base = list.map((r) => ({
            value: r.id,
            label: (r && (r.name ?? r.roleName ?? r.label)) || r.id,
        }));
        const current = employeeForEdit?.roles?.[0];
        const currentId = current && (current.id ?? current.roleId);
        let options = base;
        if (currentId && !base.some((o) => o.value === currentId)) {
            const currentLabel = (current.name ?? current.roleName ?? current.label) || currentId;
            options = [{ value: currentId, label: currentLabel }, ...base];
        }
        const byLabel = new Map();
        options.forEach((o) => {
            const key = (o.label || o.value || '').toString().toLowerCase();
            if (!byLabel.has(key)) byLabel.set(key, o);
            else if (currentId && o.value === currentId) byLabel.set(key, o);
        });
        return [...byLabel.values()];
    }, [roles, employeeForEdit]);

    const statusOptions = [
        { value: 'active', label: 'Active' },
        { value: 'invited', label: 'Invited' },
        { value: 'suspended', label: 'Suspended' },
        { value: 'deactivated', label: 'Deactivated' },
    ];

    return {
        // data
        employees,
        total,
        activeCount,
        filteredEmployees,
        tableData,
        columns,
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        // filters
        filterRole,
        setFilterRole,
        filterStatus,
        setFilterStatus,
        filterRoleOptions,
        filterStatusOptions,
        activeFilterCount,
        clearFilters,
        // panel
        panelOpen,
        setPanelOpen,
        selectedEmployeeId,
        setSelectedEmployeeId,
        selectedEmployeeName,
        openPanel,
        closePanel,
        // create employee
        canCreate,
        createModalOpen,
        setCreateModalOpen,
        createForm,
        onSubmitCreate,
        closeCreateModal,
        createEmployeePending: createEmployeeMutation.isPending,
        roleOptions,
        statusOptions,
        // edit employee
        canRemove,
        canRequestHrRemoval,
        editModalOpen,
        setEditModalOpen,
        openEditModal,
        closeEditModal,
        editForm,
        employeeForEdit,
        onSubmitEdit,
        updateEmployeePending: updateEmployeeMutation.isPending,
        // request removal
        removalConfirmOpen,
        setRemovalConfirmOpen,
        employeeForRemoval,
        openRemovalConfirm,
        confirmRequestRemoval,
        closeRemovalConfirm,
        requestRemovalPending: requestRemovalMutation.isPending,
        hasPendingRemoval,
        // remove user (Company Admin)
        removeUserModalOpen,
        employeeForRemoveUser,
        closeRemoveUserModal,
        confirmRemoveUser,
        openRemoveUserConfirm,
        removeUserPending: deleteUserMutation.isPending,
        // permissions
        canView,
        canViewAll,
        canUpdate,
        canDelete,
        canRemoveUser,
        // error state (e.g. 404 when HR API not available)
        employeesError,
        // schedules
        schedulesList,
        // helpers
        capFirst,
    };
};
